import type { CanvasEvent, HoverEvent } from '../CanvasEvent';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat } from '../SurfaceBase';
import VertexObject from '../VertexObject';
import { getArrowPath, getArrowScale, ZOOM_THRESHOLD } from './common';

export default class DrawArrowObject extends CanvasObject {
  pointerVertex: VertexObject;
  vertices: VertexObject[] = [];
  _onArrow: (p: LngLat[]) => void;
  pointer: Path2D = getArrowPath();

  constructor(id: string, surface: SurfaceBase, onArrow: (p: LngLat[]) => void) {
    super(id, surface);

    this.pointerVertex = new VertexObject(`arrow-pointer-vertex`, this.surface, {
      point: { x: NaN, y: NaN },
      lngLat: { lng: NaN, lat: NaN },
    });

    this.addChild(this.pointerVertex);
    this._onArrow = onArrow;
  }

  onArrow = () => {
    const arrowPoints = [...this.vertices.map((v) => v.lngLat)];
    this._onArrow(arrowPoints);
  };

  onSurfaceTap = (e: CanvasEvent) => {
    const v = new VertexObject(`v-${this.vertices.length}`, this.surface, {
      point: e.point,
      lngLat: e.lngLat,
    });

    this.vertices.push(v);
    this.surface.addObject(v);

    if (this.vertices.length > 1) {
      this.onArrow();
    }
  };

  onPointerMove = (e: HoverEvent) => {
    this.pointerVertex.setCoords(e);
    this.surface.renderAll();
  };

  onKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && this.vertices.length > 0) {
      this.onArrow();
    }
  };

  renderPointer = (ctx: CanvasRenderingContext2D) => {
    ctx.save();
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'red';
  };

  render(
    ctx: CanvasRenderingContext2D,
    _strokeColor?: string | undefined,
    _strokeWidth?: number | undefined,
    _isDashed?: boolean | undefined,
    _strokeOpacity?: number | undefined,
    _isBorderHidden?: boolean | undefined,
  ): void {
    if (this.vertices.length === 0) return;

    const p1 = this.vertices[0].point;
    const p2 = this.pointerVertex.point;
    const zoom = this.surface.map.getZoom();

    const dAx = p2.x - p1.x;
    const dAy = p2.y - p1.y;
    const lineAngle = -1 * Math.atan2(dAx, dAy) + Math.PI;

    ctx.lineWidth = 2;
    ctx.strokeStyle = 'red';
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();

    ctx.save();
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.translate(p2.x, p2.y);

    if (zoom <= ZOOM_THRESHOLD) {
      const s = getArrowScale(zoom);
      ctx.scale(s, s);
    }

    ctx.rotate(lineAngle);
    ctx.stroke(this.pointer);
    ctx.restore();
  }

  drawHitArea(_ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, _color: string): void {}
}
